<template>
  <div>
    <div class="col-12 px-0">
      <h1 class="d-flex align-items-center">
        <span class="icon mr-2 icon-editieren" aria-hidden="true"></span>
        {{ $t('views.timeSlotBook.title') }}
      </h1>
    </div>

    <link-back :to="{ name: 'admin-time-slot-edit', params: { roomId: roomId, timeSlotId: timeSlotId } }" i18nMessage="common.backToTimeSlot" class="mt-2" />

    <loading-container :state="state" >
      <form @submit.prevent="onSubmit()">

        <dl class="w-100">
          <dt>{{ $t('views.timeSlotBook.room') }}</dt>
          <dd>{{ room.name || '' }}</dd>
          <dt>{{ $t('views.timeSlotBook.dateTime') }}</dt>
          <dd>{{ timeSlotDateFormatted || '' }}</dd>
        </dl>

        <control-select
          i18nLabel="common.owner.salutation"
          :validation="v$.salutation"
          v-model="salutation"
          :availableValues="salutationOptions()"
        />
        <control-input
          i18nLabel="common.owner.title"
          :validation="v$.title"
          v-model="title"
        />
        <control-input
          i18nLabel="common.owner.givenName"
          :validation="v$.givenName"
          v-model="givenName"
        />
        <control-input
          i18nLabel="common.owner.surName"
          :validation="v$.surName"
          v-model="surName"
        />
        <control-input
          i18nLabel="common.owner.organization"
          :validation="v$.organization"
          v-model="organization"
        />
        <control-input
          i18nLabel="common.owner.email"
          :validation="v$.email"
          v-model="email"
          type="email"
        />
        <control-input
          i18nLabel="common.owner.phone"
          :validation="v$.phone"
          v-model="phone"
          type="tel"
        />
        <control-checkbox
          i18nLabel="common.owner.accessible"
          :validation="v$.accessible"
          v-model="accessible"
        />

        <div class="col-12 col-md-6 offset-md-3 mt-4 px-xs-0">
          <button-save i18nMessage="views.timeSlotBook.book" class="btn-w-100" />
        </div>

      </form>
    </loading-container>
  </div>
</template>

<script lang="ts">
import ControlCheckbox from '@/components/controls/ControlCheckbox.vue';
import ControlInput from '@/components/controls/ControlInput.vue';
import ControlSelect from '@/components/controls/ControlSelect.vue';
import ButtonSave from '@/components/interaction/ButtonSave.vue';
import LinkBack from '@/components/interaction/LinkBack.vue';
import LoadingContainer from '@/components/structure/LoadingContainer.vue';
import { Room, TicketOwnerSalutationEnum, TimeSlot } from '@/generated/openapi';
import LoadingMixin from '@/mixins/LoadingMixin';
import { InfoPopupBase, InfoPopupType } from '@/models/InfoPopupData';
import { PersonalData } from '@/models/PersonalData';
import { SelectOption } from '@/models/SelectOption';
import { adminService } from '@/services/adminService';
import { formatTicketTime } from '@/utils/formatUtils';
import { createSalutationOptions, validateAndFocusInvalid } from '@/utils/formUtils';
import { LoadingState } from '@/utils/loadingUtils';
import { smartBack, urlParamString } from '@/utils/navigationUtils';
import { isTelephoneString } from '@/utils/validationUtils';
import useVuelidate from '@vuelidate/core';
import { email, maxLength, required } from '@vuelidate/validators';
import { defineComponent } from 'vue';


export default defineComponent({
  name: 'TimeSlotBook',
  mixins: [
    LoadingMixin
  ],
  components: {
    LoadingContainer,
    ButtonSave,
    LinkBack,
    ControlCheckbox,
    ControlInput,
    ControlSelect
  },
  setup() {
    return {
      v$: useVuelidate()
    }
  },
  data() {
    return {
      roomId: '',
      timeSlotId: '',
      room: {} as Room,
      timeSlot: {} as TimeSlot,
      salutation: TicketOwnerSalutationEnum.None,
      title: '',
      givenName: '',
      surName: '',
      organization: '',
      email: '',
      phone: '',
      accessible: false
    }
  },
  validations() {
    return {
      salutation: {
      },
      title: {
        maxLength: maxLength(40)
      },
      givenName: {
        required,
        maxLength: maxLength(40)
      },
      surName: {
        required,
        maxLength: maxLength(40)
      },
      organization: {
        maxLength: maxLength(40)
      },
      email: {
        email,
        maxLength: maxLength(320)
      },
      phone: {
        isTelephoneString,
        maxLength: maxLength(40)
      },
      accessible: {
      },
    }
  },
  created() {
    this.roomId = urlParamString(this.$route, 'roomId')
    this.timeSlotId = urlParamString(this.$route, 'timeSlotId')
    Promise.all([
      adminService.getRoom(this.roomId),
      adminService.getTimeSlot(this.roomId, this.timeSlotId)
    ]).then(([room, timeSlot]) => {
      this.room = room
      this.timeSlot = timeSlot
      this.state = LoadingState.success
    }).catch(this.handleError)
  },
  computed: {
    timeSlotDateFormatted(): string {
      return formatTicketTime(this.timeSlot)
    }
  },
  methods: {
    salutationOptions(): SelectOption[] {
      return createSalutationOptions()
    },
    onSubmit() {
      validateAndFocusInvalid(this.v$, this.$el).then(this.bookTicket)
    },
    bookTicket() {
      adminService.bookTicket(this.timeSlotId, this.asPersonalData())
        .then(() => {
          this.$store.commit('addInfoPopup', {
            title: this.$t('views.timeSlotBook.success', { givenName: this.givenName, surName: this.surName }),
            type: InfoPopupType.info
          } as InfoPopupBase)
          smartBack(this.$router, {
            name: 'admin-time-slot-edit',
            params: {
              roomId: this.roomId,
              timeSlotId: this.timeSlotId
              }
          })
        }).catch(this.handleErrorWithI18nString)
    },
    asPersonalData(): PersonalData {
      return {
        salutation: this.salutation,
        title: this.title,
        givenName: this.givenName,
        surName: this.surName,
        organization: this.organization,
        email: this.email,
        phone: this.phone,
        accessible: this.accessible
      }
    },
  }
});
</script>

<style scoped>
dt {
  float: left;
  font-weight: bold;
  margin-right: 0.5em;
}
dd::after {
  clear: left;
  content: " ";
  display: block;
}
@media screen and (max-width: 420px){
  dt{
    width: 100%;
  }
  dd{
    width: 100%;
    margin-inline-start: 0px;
  }
}
</style>
